import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import FilterSelect from "../forms/FilterSelect";
import { filterOrgTreeRows, orgTreeRows } from "../../utils/helpers/orgTree";

const SchoolIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-5h6v5" />
  </svg>
);

// School picker for org-subtree viewers (head-org staff / superusers).
// A non-leaf (head) org aggregates its whole subtree on the backend, so it is
// labelled "Name (all schools)" to make the rollup explicit. The list is the
// org tree (see orgTreeRows): head office, then each region over its schools.
// Self-hides until there is a real choice to make.
export default function OrgSelect({ organizations, value, onChange, className = "" }) {
  const { t } = useTranslation();

  const options = useMemo(
    () =>
      orgTreeRows(organizations).map((row) => ({
        ...row,
        value: row.uuid,
        label: row.isHead
          ? `${row.name} (${t("dashboard.allSchoolsSuffix")})`
          : row.name,
        strong: row.isHead,
      })),
    [organizations, t],
  );

  if (!organizations || organizations.length < 2) return null;

  return (
    <FilterSelect
      // Shrinks to the room the header has rather than growing with the name:
      // a long school name would otherwise wrap the header onto a second row
      // the moment it is picked, and push the language switch off a phone.
      className={`min-w-0 max-w-full ${className}`}
      // A <button> sizes to its content, not its box — without w-full the
      // wrapper shrinks and the trigger spills over the toggles beside it.
      minWidthClass="w-full min-w-[12rem]"
      options={options}
      value={value || ""}
      onChange={onChange}
      icon={SchoolIcon}
      ariaLabel={t("dashboard.school")}
      placeholder={t("dashboard.school")}
      // A single school keeps a bare menu; a network of 60 is a list you scroll
      // knowing the name you want. Same threshold as the timetable pickers.
      searchable={options.length > 8}
      filterOptions={filterOrgTreeRows}
      searchPlaceholder={t("common.search")}
      emptyText={t("dashboard.noSchoolMatches")}
    />
  );
}
